/**
 * The needs floor (§9): you have to eat, you have to sleep somewhere.
 *
 * Nothing is ticked. Needs are stored as the last time they were met and
 * evaluated lazily, at the moment something asks (§10). "Trivial when up,
 * brutal when down": a fed, rested player pays nothing; a starving,
 * exhausted one loses the use of their body. Injury does the same thing
 * through its own state (§17). None of this is a health bar (§22) — it is a
 * set of conditions that close options.
 */

import { minutesBetween } from './time.js';
import { TUNING } from './tuning.js';
import type { StatName, StatValue, Stats, WorldState, WorldTime } from './types/index.js';

export type HungerLevel = 'fed' | 'hungry' | 'starving';
export type FatigueLevel = 'rested' | 'tired' | 'exhausted';

export interface NeedsStatus {
  readonly hunger: HungerLevel;
  readonly fatigue: FatigueLevel;
  readonly minutesSinceAte: number;
  readonly minutesSinceSlept: number;
  /** Do you have a bed at all. Null lodging is homelessness (§9). */
  readonly sheltered: boolean;
}

export function hungerLevel(minutesSinceAte: number): HungerLevel {
  if (minutesSinceAte >= TUNING.needs.starvingAfter) return 'starving';
  if (minutesSinceAte >= TUNING.needs.hungryAfter) return 'hungry';
  return 'fed';
}

export function fatigueLevel(minutesSinceSlept: number): FatigueLevel {
  if (minutesSinceSlept >= TUNING.needs.exhaustedAfter) return 'exhausted';
  if (minutesSinceSlept >= TUNING.needs.tiredAfter) return 'tired';
  return 'rested';
}

/** Where the body stands right now, or at `at`. */
export function needsStatus(state: WorldState, at: WorldTime = state.time): NeedsStatus {
  const { needs, lodging } = state.player;
  const minutesSinceAte = Math.max(0, minutesBetween(needs.lastAte, at));
  const minutesSinceSlept = Math.max(0, minutesBetween(needs.lastSlept, at));
  return {
    hunger: hungerLevel(minutesSinceAte),
    fatigue: fatigueLevel(minutesSinceSlept),
    minutesSinceAte,
    minutesSinceSlept,
    sheltered: lodging !== null,
  };
}

/**
 * What the needs floor takes off you, as stat deltas. Stats are affordance
 * gates, so "brutal when down" means the gates close: a starving man's body
 * fails him, an exhausted one stops noticing things.
 */
// PROVISIONAL: which stats and by how much is tuning.
export function needsModifiers(status: NeedsStatus): Partial<Record<StatName, number>> {
  const mods: Partial<Record<StatName, number>> = {};
  if (status.hunger === 'starving') mods.body = (mods.body ?? 0) - 1;
  if (status.fatigue === 'exhausted') {
    mods.body = (mods.body ?? 0) - 1;
    mods.read = (mods.read ?? 0) - 1;
  }
  return mods;
}

const STAT_NAMES: readonly StatName[] = ['nerve', 'read', 'presence', 'body'];

function clampStat(value: number): StatValue {
  return Math.max(1, Math.min(5, Math.round(value))) as StatValue;
}

/**
 * The player's stats as they stand: the sheet, minus what hunger, exhaustion
 * and injury are costing right now. The sheet itself never changes here —
 * growth is by use and slow (§5), and that is not this module's job.
 */
export function effectiveStats(state: WorldState, at: WorldTime = state.time): Stats {
  const base = state.player.stats;
  const mods = needsModifiers(needsStatus(state, at));
  const out: Record<StatName, StatValue> = { ...base };
  for (const stat of STAT_NAMES) {
    let value: number = base[stat] + (mods[stat] ?? 0);
    for (const injury of state.player.injuries) {
      if (injury.duration.kind === 'temporary' && minutesBetween(injury.duration.healsOn, at) >= 0) {
        continue; // healed — resolved lazily, never removed by a tick
      }
      for (const effect of injury.effects) {
        if (effect.kind === 'stat' && effect.stat === stat) value += effect.delta;
      }
    }
    out[stat] = clampStat(value);
  }
  return out;
}
